import { ReactNode } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type SidebarButtonProps = {
  icon?: ReactNode;
  label: string;
  active?: boolean;
  caret?: "up" | "down";
  right?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  testId?: string;
  className?: string;
};

export function SidebarButton({
  icon,
  label,
  active,
  caret,
  right,
  disabled,
  onClick,
  testId,
  className
}: SidebarButtonProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 min-h-8 w-full p-2 rounded-lg text-left transition-colors outline-hidden focus:outline-hidden focus-visible:outline-hidden",
        "text-[14px] leading-4 font-medium text-innoveraZinc-600 hover:bg-innoveraZinc-50",
        active && "bg-innoveraZinc-100 text-innoveraZinc-900",
        disabled && "text-innoveraZinc-400 hover:bg-transparent cursor-default",
        className
      )}
    >
      {icon && <span className="shrink-0 inline-flex">{icon}</span>}
      <span className="flex-1 min-w-0 line-clamp-2">{label}</span>
      {right}
      {caret &&
        (caret === "up" ? (
          <CaretUp className="size-4 shrink-0" weight="regular" />
        ) : (
          <CaretDown className="size-4 shrink-0" weight="regular" />
        ))}
    </button>
  );
}

export function SidebarGroupLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center h-8 px-2">
      <span className="block overflow-hidden whitespace-nowrap text-[10px] leading-4 font-medium uppercase tracking-[0.6px] text-innoveraZinc-500">
        {label}
      </span>
    </div>
  );
}

export function SidebarSubMenu({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-all duration-300 ease-in-out",
        expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      )}
    >
      <div className="overflow-hidden">
        <div className="relative flex flex-col gap-px pl-8 py-1">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-innoveraZinc-150" />
          {children}
        </div>
      </div>
    </div>
  );
}
